import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { avatarCollectionKeys, socialProofAvatarKeys } from '@/src/constants/socialAvatar.constant';
import ClassNames from '@/src/components/componentHelper/ClassNames';

import EmbedLoader from '../lib/EmbedLoader';
import { getEmbedOrigin } from '../lib/origin';

const getAvatarPixelSize = (value) => {
  switch (value) {
    case 6:
      return 24;
    case 8:
      return 32;
    case 10:
      return 40;
    case 12:
      return 48;
    case 14:
      return 56;
    default:
      return 40;
  }
};

const getLogoPixelSize = (value) => {
  switch (value) {
    case 6:
      return 24;
    case 8:
      return 28;
    case 10:
    case 12:
    case 14:
      return 32;
    default:
      return 32;
  }
};

const getPowerPixelSize = (value) => {
  switch (value) {
    case 6:
      return 20;
    case 8:
    case 10:
    case 12:
    case 14:
      return 28;
    default:
      return 28;
  }
};

// Faithful no-iframe port of pages/avatars/[collectionId].js. The markup, state
// and sizing logic are kept identical; only the data source (getServerSideProps
// / getSocialAvatars -> /api/seo-embed-avatars) is swapped, the page loader is
// replaced with the embed loader, and root-relative logo assets are rebased
// onto the embed origin. requestParentResize stays a no-op (no iframe here),
// exactly like SeoSliderView.
export default function SeoAvatarsView({ initialData }) {
  const imageAvatarUrls = initialData.results;
  const collection = initialData.wall;

  const [render, setRender] = useState(false);
  const isCompactViewport = useMediaQuery({ maxWidth: 768 });
  const avatarUrls = Array.isArray(imageAvatarUrls) ? imageAvatarUrls : [];
  const avatarCount = avatarUrls.length;

  useEffect(() => {
    setRender(true);
  }, []);

  const requestParentResize = () => {
    if (typeof window === 'undefined' || !window.parentIFrame?.size) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.parentIFrame.size();
    });
  };

  useEffect(() => {
    if (!render || !collection || typeof window === 'undefined') {
      return undefined;
    }

    requestParentResize();
    const timeoutId = window.setTimeout(requestParentResize, 150);
    window.addEventListener('resize', requestParentResize);

    const contentElement = document.getElementById('content');
    let resizeObserver;

    if (contentElement && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        requestParentResize();
      });

      resizeObserver.observe(contentElement);
    }

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', requestParentResize);
      resizeObserver?.disconnect();
    };
  }, [render, collection, avatarCount, isCompactViewport]);

  if (!render || !collection) {
    return <EmbedLoader />;
  }

  if (!avatarCount) {
    return null;
  }

  const baseAvatarSize = getAvatarPixelSize(collection[avatarCollectionKeys.imageSize]);
  const shouldUseCompactLayout = isCompactViewport;
  const avatarSize = baseAvatarSize;
  const logoSize = getLogoPixelSize(collection[avatarCollectionKeys.imageSize]);
  const powerSize = getPowerPixelSize(collection[avatarCollectionKeys.imageSize]);

  const subtitleText = collection[avatarCollectionKeys.subtitleText]?.trim() || '';
  const hasSubtitle = subtitleText !== '';
  const subtitlePosition = collection[avatarCollectionKeys.subtitlePosition] || 'bottom';
  const embedAlignPosition = collection[avatarCollectionKeys.embedAlignPosition] || 'left';
  const showLogo = !collection[avatarCollectionKeys.isLogoDisabled];

  // When the embed is left-aligned on a wide (non-compact) container, the
  // subtitle text and avatar row sit flush-left instead of centered within
  // the block. Compact/mobile keeps its existing centered layout.
  const alignLeft = embedAlignPosition === 'left' && !shouldUseCompactLayout;

  const subtitleStyle = {
    margin: 0,
    maxWidth: '100%',
    fontSize: '16px',
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
    textAlign: alignLeft ? 'left' : 'center',
  };

  const avatarStyle = {
    height: avatarSize,
    width: avatarSize,
    minHeight: avatarSize,
    minWidth: avatarSize,
    maxHeight: avatarSize,
    maxWidth: avatarSize,
    objectFit: 'cover',
    flexShrink: 0,
  };

  const avatarOverlap = 4;

  const avatarElements = avatarUrls.map((url, index) => {
    const href = url[socialProofAvatarKeys.authorLink];
    const key = url[socialProofAvatarKeys.uuid] || index;
    const hasHref = Boolean(href);
    const avatarWrapperStyle = {
      display: 'inline-flex',
      marginLeft: index === 0 ? 0 : `-${avatarOverlap}px`,
      position: 'relative',
      zIndex: index + 1,
    };
    const avatarImage = (
      <img
        height={avatarSize}
        width={avatarSize}
        style={avatarStyle}
        className={`inline-block rounded-full ring-2 ring-white ${
          hasHref ? 'cursor-pointer' : 'cursor-default'
        }`}
        src={url[socialProofAvatarKeys.authorImageUrl]}
        alt={url[socialProofAvatarKeys.authorTagline] || 'Avatar'}
        onLoad={requestParentResize}
      />
    );

    if (!hasHref) {
      return (
        <span key={key} style={avatarWrapperStyle}>
          {avatarImage}
        </span>
      );
    }

    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        style={avatarWrapperStyle}
      >
        {avatarImage}
      </a>
    );
  });

  const logoElement = showLogo ? (
    <div className="flex ml-1">
      <a
        href="https://famewall.io?ref=miniWidget"
        target="_blank"
        rel="noreferrer noopener"
        className="flex items-center duration-100 rounded-md no-underline"
        style={{ textDecoration: 'none' }}
      >
        <Image
          src={`${getEmbedOrigin()}/Lightning.png`}
          placeholder="blur"
          blurDataURL={`${getEmbedOrigin()}/Lightning.png`}
          alt="Famewall logo"
          loading="lazy"
          height={powerSize}
          width={powerSize}
          onLoad={requestParentResize}
        />
        <Image
          src={`${getEmbedOrigin()}/MiniTestimonialIconFamewall.svg`}
          placeholder="blur"
          blurDataURL={`${getEmbedOrigin()}/MiniTestimonialIconFamewall.svg`}
          alt="Famewall logo"
          loading="lazy"
          height={logoSize}
          width={logoSize}
          onLoad={requestParentResize}
        />
      </a>
    </div>
  ) : null;

  const avatarStack = (
    <div className={ClassNames('flex', alignLeft ? 'justify-start' : 'justify-center')} style={{ maxWidth: '100%' }}>
      <div
        className="flex"
        style={{
          width: shouldUseCompactLayout ? '100%' : 'auto',
          maxWidth: '100%',
          flexWrap: shouldUseCompactLayout ? 'wrap' : 'nowrap',
          justifyContent: shouldUseCompactLayout ? 'center' : 'flex-start',
          rowGap: shouldUseCompactLayout ? 8 : 0,
          columnGap: 0,
          paddingLeft: shouldUseCompactLayout ? 0 : avatarOverlap,
        }}
      >
        {avatarElements}
      </div>
      {logoElement}
    </div>
  );

  const renderMethods = () => {
    if (!hasSubtitle) {
      return (
        <>
          {avatarStack}
        </>
      );
    }

    if (subtitlePosition === 'top' || subtitlePosition === 'bottom') {
      return (
        <div
          className={ClassNames(
            'flex flex-col',
            (!embedAlignPosition || embedAlignPosition === 'left')
              ? 'justify-start'
              : (embedAlignPosition === 'center')
                ? 'justify-center mx-auto'
                : ''
          )}
          style={{ maxWidth: '100%', marginTop: '12px' }}
        >
          {subtitlePosition === 'top' && (
            <p
              className="text-center text-base text-gray-900 font-roboto"
              style={{ ...subtitleStyle, marginBottom: '8px' }}
            >
              {subtitleText}
            </p>
          )}
          {avatarStack}
          {subtitlePosition === 'bottom' && (
            <p
              className="text-center text-base text-gray-900 font-roboto"
              style={{ ...subtitleStyle, marginTop: '8px' }}
            >
              {subtitleText}
            </p>
          )}
        </div>
      );
    }

    return (
      <div
        className={ClassNames(
          'flex',
          'items-center',
          (!embedAlignPosition || embedAlignPosition === 'left')
            ? 'justify-start'
            : (embedAlignPosition === 'center')
              ? 'justify-center mx-auto'
              : ''
        )}
        style={{ maxWidth: '100%', marginTop: '20px' }}
      >
        {subtitlePosition === 'left' && (
          <p
            className={ClassNames(
              'text-base text-gray-900 font-roboto',
              'align-middle mr-2 my-auto text-center'
            )}
            style={subtitleStyle}
          >
            {subtitleText}
          </p>
        )}
        {avatarStack}
        {subtitlePosition === 'right' && (
          <p
            className={ClassNames(
              'text-base text-gray-900 font-roboto',
              'text-center ml-2 my-auto'
            )}
            style={subtitleStyle}
          >
            {subtitleText}
          </p>
        )}
      </div>
    );
  };

  return (
    <div
      className="px-2"
      style={{
        backgroundColor: 'transparent',
        backgroundRepeat: 'no-repeat',
        borderBottomRightRadius: '1px',
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible',
      }}
    >
      <main className="mx-auto" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="relative" style={{ width: '100%', maxWidth: '100%' }}>
          <div
            className="relative place-items-center mobile-scaling w-full"
            style={{ width: '100%', maxWidth: '100%' }}
          >
            <div className="rounded-lg w-full relative content-shadow" style={{ overflow: 'visible' }}>
              <div
                id="content"
                className="place-items-center flex transition-colors w-full"
                style={{
                  padding: '0',
                  fontSize: 16,
                  width: '100%',
                  maxWidth: '100%',
                  justifyContent: shouldUseCompactLayout ? 'center' : 'flex-start',
                }}
              >
                {renderMethods()}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
